import { AxiosResponse } from 'axios';
import { exportFileTypes } from '../models/exportFileTypes';

export interface ExportFileParams {
  file_type?: exportFileTypes;
  filename?: string;
}

// Helper function to wait/delay
const wait = (ms: number): Promise<void> => {
  return new Promise(resolve => setTimeout(resolve, ms));
};

export const handleFileExport = async (
  response: AxiosResponse,
  params?: ExportFileParams
): Promise<void> => {
  const fileType: 'csv' | 'xls' | 'pdf' =
    params?.file_type === 'excel' ? 'xls' : params?.file_type === 'csv' ? 'csv' : 'pdf';

  const filename = params?.filename ?? 'report';
  const newDate = new Date();
  const waitTime = 1000;

  if (['csv', 'xls'].includes(fileType)) {
    const blob = new Blob([response as any], {
      type: 'application/vnd.ms-excel',
    });

    // Wait for blob processing if specified
    if (waitTime > 0) {
      await wait(waitTime);
    }

    // Create a link element, use it to download
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;

    // Optional: Set default file name
    link.setAttribute(
      'download',
      `${filename}-${newDate.getFullYear()}-${
        newDate.getMonth() + 1
      }-${newDate.getDate()}-${newDate.getHours()}-${newDate.getMinutes()}.${fileType}`
    );

    // Append to body and trigger click
    document.body.appendChild(link);
    link.click();

    // Wait before cleanup to ensure download starts
    await wait(100); // Small delay for download to initiate

    // Clean up
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } else {
    // PDF export
    const file = new Blob([response as any], { type: 'application/pdf' });

    // Wait if specified
    if (waitTime > 0) {
      await wait(waitTime);
    }

    const fileURL = URL.createObjectURL(file);
    window.open(fileURL, '_blank');

    // Optional: Clean up URL after some time
    setTimeout(() => {
      URL.revokeObjectURL(fileURL);
    }, 10000); // Clean up after 10 seconds
  }
};

export const getExportConfig = (params?: ExportFileParams) => {
  const fileType = params?.file_type ?? 'pdf';

  return {
    responseType: (['csv', 'excel'].includes(fileType) ? 'blob' : 'arraybuffer') as
      | 'blob'
      | 'arraybuffer',
    ...(fileType === 'pdf' ? { headers: { Accept: 'application/pdf' } } : {}),
    // Add timeout for longer wait times if needed
    timeout: 300000, // 5 minutes
  };
};
